// Tripwire only: agents/users override via `rekor unbind` / `rekor use`.
//
// Binds a git checkout (main or linked worktree) to a single database so
// `rekor push`/`pull` refuse to run against a different database once bound.
// NOT a concurrency lock — a per-worktree routing guard against a prompt
// landing in the wrong checkout and clobbering another preview's config.
// Mirrors wayai's hub binding (`apps/cli/src/lib/hub-binding.ts`); together
// with `.rekor.yaml` (org, repo-wide & committed) it pins both axes — the org
// for the repo, the database for this one worktree.

import { execFileSync } from 'node:child_process';
import { readFileSync, unlinkSync, writeFileSync } from 'node:fs';
import { join, resolve } from 'node:path';
import chalk from 'chalk';

const BINDING_FILENAME = 'rekor-binding';

// A database id is a slug (`my-db`, `prod--my-preview`), never a UUID. Keep the
// validity gate permissive enough to accept any real id but strict enough that a
// garbled/multi-line file reads as unbound (so the tripwire fails open to "no
// binding" rather than wedging on corrupt state). No whitespace, no path
// separators, bounded length. The leading `(?!\.\.?$)` rejects the two
// filesystem-special names `.` and `..` — the regex already blocks `/`, but a bare
// `..` is still a traversal segment that would resolve `join(dir, id)` to the parent
// dir; refusing it keeps any code that writes a server-provided id into a path
// (e.g. the production-mirror `.gitignore`, #570) from escaping the intended tree.
const DATABASE_ID_RE = /^(?!\.\.?$)[A-Za-z0-9._-]{1,256}$/;

/**
 * True when `id` is a structurally valid database id (slug — no separators/whitespace, not `.`/`..`,
 * bounded length). Shared guard so any code that writes a server-provided id into a filesystem path
 * can reject a malformed value before joining it (blocks `/` and the `.`/`..` traversal segments, so
 * path traversal isn't reachable).
 */
export function isValidBaseId(id: string): boolean {
  return DATABASE_ID_RE.test(id);
}

// Cache keyed by cwd so tests (and a long-lived process) re-resolve after a chdir
// between checkouts.
let _gitDirCache: { cwd: string; gitDir: string | null } | undefined;

function resolveGitDir(): string | null {
  const cwd = process.cwd();
  if (_gitDirCache && _gitDirCache.cwd === cwd) return _gitDirCache.gitDir;

  let gitDir: string | null;
  try {
    // `--git-dir` resolves to the per-worktree git dir (`.git/worktrees/<name>`
    // for a linked worktree), which is exactly what makes the binding worktree-local.
    const raw = execFileSync('git', ['rev-parse', '--git-dir'], {
      cwd,
      encoding: 'utf-8',
      stdio: ['pipe', 'pipe', 'pipe'],
    }).trim();
    gitDir = raw ? resolve(cwd, raw) : null;
  } catch {
    gitDir = null;
  }
  _gitDirCache = { cwd, gitDir };
  return gitDir;
}

/** Absolute path to this worktree's binding file, or null when not inside a git repo. */
export function getBindingPath(): string | null {
  const gitDir = resolveGitDir();
  return gitDir ? join(gitDir, BINDING_FILENAME) : null;
}

/**
 * The database id this worktree is bound to, or null when unbound / not in a repo.
 * Surfaces non-ENOENT errors (e.g. EACCES) so an unreadable binding can't masquerade
 * as unbound; a present-but-corrupt file reads as null (treated as unbound).
 */
export function readBinding(): string | null {
  const bindingPath = getBindingPath();
  if (!bindingPath) return null;
  let raw: string;
  try {
    raw = readFileSync(bindingPath, 'utf-8');
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return null;
    throw err;
  }
  const trimmed = raw.trim();
  return DATABASE_ID_RE.test(trimmed) ? trimmed : null;
}

/** Bind this worktree to `baseId`. Throws if not in a git repo or the id is malformed. */
export function writeBinding(baseId: string): void {
  if (!DATABASE_ID_RE.test(baseId)) {
    throw new Error(`Invalid database id for binding: ${JSON.stringify(baseId)}`);
  }
  const bindingPath = getBindingPath();
  if (!bindingPath) {
    throw new Error('Not inside a git repository — cannot bind this worktree to a database.');
  }
  writeFileSync(bindingPath, `${baseId}\n`, 'utf-8');
}

/** Remove this worktree's binding. Returns true if one existed. Surfaces non-ENOENT errors. */
export function clearBinding(): boolean {
  const bindingPath = getBindingPath();
  if (!bindingPath) return false;
  try {
    unlinkSync(bindingPath);
    return true;
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return false;
    throw err;
  }
}

/**
 * The routing-mismatch message for a command that resolved to `resolvedId` while this worktree is
 * bound to a different database — or null when there's no binding or it matches (the OK paths).
 * Pure (no I/O beyond the binding read, no exit) so the decision is unit-testable.
 */
export function bindingMismatch(resolvedId: string): string | null {
  const bound = readBinding();
  if (!bound || bound === resolvedId) return null;
  return [
    `This worktree is bound to database ${bound}, but this command resolved to ${resolvedId}.`,
    '',
    'This usually means a prompt was routed to the wrong worktree.',
    'Confirm with the user before unbinding. To override:',
    '  rekor unbind             # clear the binding for this worktree',
    `  rekor use ${resolvedId}  # rebind this worktree to ${resolvedId}`,
  ].join('\n');
}

/** Hard-stop a push/pull whose resolved database doesn't match this worktree's binding. */
export function assertBaseMatchesBinding(resolvedId: string): void {
  const message = bindingMismatch(resolvedId);
  if (message) {
    // Deliberately bypasses the thrown-error pipeline: this is an expected routing guard, not a
    // bug — printing directly (like `helpers.ts:getBase`) keeps it out of telemetry/Sentry.
    console.error(message);
    process.exit(1);
  }
}

/** Hard-stop creating a brand-new preview in a worktree already bound to a different database. */
export function assertNoBindingBlocksBaseCreation(newName: string): void {
  const bound = readBinding();
  if (!bound) return;
  console.error(
    [
      `This worktree is bound to database ${bound}, but you're about to create a new preview "${newName}" here.`,
      '',
      'This usually means a prompt was routed to the wrong worktree.',
      'Confirm with the user before unbinding. To override:',
      '  rekor unbind             # clear the binding for this worktree',
    ].join('\n'),
  );
  process.exit(1);
}

/**
 * Bind this worktree to `baseId` if it isn't already bound (auto-set on the first successful
 * push/pull, like wayai). Best-effort: a failure to persist must not fail the parent operation
 * that already succeeded. Returns true if it wrote a new binding.
 */
export function autoBindIfUnbound(baseId: string): boolean {
  try {
    if (readBinding()) return false;
    writeBinding(baseId);
    console.log(chalk.dim(`Worktree bound to database ${baseId} (run 'rekor unbind' to clear).`));
    return true;
  } catch (err) {
    console.warn(chalk.dim(`  Note: could not bind this worktree (${err instanceof Error ? err.message : String(err)}).`));
    return false;
  }
}
